import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from 'src/api/client';
import { useCreateSpeakerProfile, useSpeakerProfiles } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { PendingCommands } from 'src/components/editorial/PendingCommands';
import { UploadClips } from 'src/components/editorial/UploadClips';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Disclosure } from 'src/components/ui/Disclosure';
import { Skeleton } from 'src/components/ui/Skeleton';
import { PROFILE_STATUS_LABEL, PROFILE_STATUS_MEANING, PROFILE_STATUS_TONE, profileStrength, sortProfiles } from 'src/lib/speakerProfiles';
import { formatDateTime } from 'src/lib/time';
import type { EditorialSpeakerProfile } from 'src/types/editorial';

/**
 * Every voice the system knows, and which of them still need a person.
 *
 * The panel is built around one asymmetry: the pipeline is good at noticing that two segments are
 * the same person and incapable of knowing who that person is. Naming a voice, and feeding it audio
 * before it is ever heard live, is the whole job — so both are on the surface rather than behind a
 * disclosure. The previous build had three collapsed triangles stacked above the roster, and the
 * one that created a voice looked exactly like the one that explained the feature.
 */
export function VoiceProfilesWorkspace() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const profiles = useSpeakerProfiles(includeArchived);

  const rows = useMemo(() => sortProfiles(profiles.data?.profiles ?? []), [profiles.data]);
  const unnamed = rows.filter(profile => profile.status === 'pending_label').length;
  const pendingCommands = profiles.data?.commands?.filter(command => command.status === 'pending').length ?? 0;

  if (profiles.isPending) {
    return (
      <div className="page-wide">
        <Skeleton variant="rows" count={6} />
      </div>
    );
  }

  if (profiles.isError) {
    return (
      <div className="page-wide">
        <LoadFailure
          title="No se pudo cargar el panel de voces"
          error={profiles.error}
          onRetry={profiles.refetch}
          whenMissing="El panel de voces necesita una versión del API que quizá aún no está desplegada."
        />
      </div>
    );
  }

  return (
    <div className="page-wide">
      <PageHeader
        title="Voces"
        actions={
          <>
            <label className="ledger-head__check">
              <input type="checkbox" checked={includeArchived} onChange={event => setIncludeArchived(event.target.checked)} />
              Incluir archivadas
            </label>
            <Button variant="primary" onClick={() => setCreating(value => !value)} aria-expanded={creating}>
              Crear voz
            </Button>
          </>
        }
      >
        El sistema agrupa por sí solo los segmentos que suenan a la misma persona, pero no puede saber de quién se
        trata. Ponle nombre a una voz y quedará identificada en todas las transcripciones siguientes; súbele audio y la
        reconocerá desde la primera vez que hable.
      </PageHeader>

      {creating && <CreateVoice onDone={() => setCreating(false)} />}

      {/* Standing before roster: what needs a person, and what the box has not applied yet. Both are
          counts rather than lists — the roster below is the list. */}
      {(unnamed > 0 || pendingCommands > 0) && (
        <p className="voices__standing">
          {unnamed > 0 && (
            <span className="voices__standingItem" data-tone="partial">
              <strong>{unnamed}</strong> {unnamed === 1 ? 'voz espera un nombre' : 'voces esperan un nombre'}
            </span>
          )}
          {pendingCommands > 0 && (
            <span className="voices__standingItem" data-tone="unresolved">
              <strong>{pendingCommands}</strong> {pendingCommands === 1 ? 'cambio en cola' : 'cambios en cola'} · se aplican en la
              siguiente corrida
            </span>
          )}
        </p>
      )}

      {rows.length === 0 ? (
        <AbsenceState
          kind="empty"
          what="No hay voces registradas todavía."
          why="Se crean solas al transcribir: la primera vez que aparece una voz desconocida queda guardada aquí."
          wouldChange="También puedes crear una a mano y subirle audio, y quedará lista antes de su primera transmisión."
        />
      ) : (
        <table className="voice-table">
          <caption className="visually-hidden">Voces registradas, las que esperan nombre primero</caption>
          {/* Explicit widths: the numeric columns are content-sized so the figures sit under the
              word that labels them, which is the whole point of a ledger. */}
          <colgroup>
            <col className="voice-table__colName" />
            <col className="voice-table__colState" />
            <col className="voice-table__colClips" />
            <col className="voice-table__colMatches" />
            <col className="voice-table__colWhen" />
            <col className="voice-table__colStrength" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Voz</th>
              <th scope="col">Estado</th>
              <th scope="col" className="voice-table__num">
                Clips
              </th>
              <th scope="col" className="voice-table__num">
                Reconocidos
              </th>
              <th scope="col">Última vez</th>
              <th scope="col">Huella</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(profile => (
              <VoiceRow key={profile.profileId} profile={profile} />
            ))}
          </tbody>
        </table>
      )}

      <div className="quiet-tail">
        <PendingCommands commands={profiles.data?.commands ?? []} />

        <Disclosure summary="Cómo aprende el sistema una voz">
          <ol className="voices__how">
            <li>Durante la transcripción, cada fragmento de audio se convierte en una huella numérica.</li>
            <li>Si se parece lo suficiente a una voz conocida, el segmento queda etiquetado con ese nombre.</li>
            <li>Si no se parece a ninguna, el sistema crea una voz nueva con nombre provisional y guarda el audio que la originó.</li>
            <li>
              La huella de una voz es el promedio de sus clips incluidos. Excluir un clip la recalcula, y volver a
              incluirlo la devuelve exactamente a donde estaba.
            </li>
            <li>Los cambios se aplican en la siguiente corrida: el audio y el modelo de voz viven en la máquina que procesa.</li>
          </ol>
        </Disclosure>
      </div>
    </div>
  );
}

/**
 * Two steps, numbered, because they are a sequence and the second is what makes the first useful.
 *
 * A voice created without audio recognises nobody — it is a name waiting for a broadcast. Asking
 * the editor to navigate to a detail page to finish is how a profile ends up empty, so the upload
 * appears in the same panel the moment the name is accepted.
 */
function CreateVoice({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [created, setCreated] = useState<{ profileId: string; name: string } | null>(null);
  const create = useCreateSpeakerProfile();

  return (
    <section className="create-voice" aria-label="Crear una voz">
      <ol className="create-voice__steps">
        <li className="create-voice__step" data-done={created ? 'true' : 'false'}>
          <p className="create-voice__stepHead">
            <span className="create-voice__stepNumber">1</span> Quién es
          </p>

          {created ? (
            <p className="create-voice__done">
              <strong>{created.name}</strong> quedó en cola.
            </p>
          ) : (
            <form
              className="create-voice__form"
              onSubmit={event => {
                event.preventDefault();
                create.mutate(
                  { name: name.trim() },
                  { onSuccess: result => setCreated({ profileId: result.profileId, name: name.trim() }) }
                );
              }}
            >
              <label className="create-voice__field">
                <span>Nombre de la persona</span>
                <input
                  type="text"
                  value={name}
                  placeholder="Omar García Harfuch"
                  onChange={event => setName(event.target.value)}
                  required
                  minLength={2}
                  autoFocus
                />
              </label>
              <Button type="submit" variant="primary" busy={create.isPending} disabled={name.trim().length < 2}>
                Continuar
              </Button>
              {create.isError && (
                <p className="voice-actions__error">{create.error instanceof ApiError ? create.error.message : 'No se pudo crear la voz'}</p>
              )}
            </form>
          )}
        </li>

        <li className="create-voice__step" data-enabled={created ? 'true' : 'false'}>
          <p className="create-voice__stepHead">
            <span className="create-voice__stepNumber">2</span> Cómo suena
          </p>
          {created ? (
            <UploadClips
              profileId={created.profileId}
              note="Se entrenan en la misma corrida en que se cree la voz. Diez segundos a dos minutos por archivo, de esa persona hablando sola."
            />
          ) : (
            <p className="create-voice__pending">
              Después de nombrarla, súbele audio suyo aquí. Sin audio la voz existe pero no reconoce a nadie.
            </p>
          )}
        </li>
      </ol>

      <div className="create-voice__foot">
        {created && (
          <Link className="create-voice__link" to={`/voces/${encodeURIComponent(created.profileId)}`}>
            Ver su ficha
          </Link>
        )}
        <Button onClick={onDone}>{created ? 'Listo' : 'Cancelar'}</Button>
      </div>
    </section>
  );
}

/** A row is a doorway or it is not (Composition §13) — the whole row opens the voice. */
function VoiceRow({ profile }: { profile: EditorialSpeakerProfile }) {
  const strength = profileStrength(profile);
  const neverMatched = profile.matchCount === 0 && profile.status === 'active';

  return (
    <tr className="voice-table__row" data-status={profile.status}>
      <th scope="row">
        <Link className="voice-table__name" to={`/voces/${encodeURIComponent(profile.profileId)}`}>
          {profile.name}
        </Link>
        {/* The panel's clearest finding: a voice that has been around and never matched anything is
            either a duplicate of another or someone who stopped appearing. */}
        {neverMatched && <span className="voice-table__flag">nunca ha reconocido nada · quizá duplicada</span>}
        {/* On a phone the strength column is dropped rather than squeezed — Composition §13 forbids
            giving the table a scrollbar — so it reappears here, under the name it describes. */}
        <span className="voice-table__compact">
          {profile.clipCount} {profile.clipCount === 1 ? 'clip' : 'clips'} · {strength.label.toLowerCase()}
        </span>
      </th>
      <td>
        <Badge tone={PROFILE_STATUS_TONE[profile.status]} title={PROFILE_STATUS_MEANING[profile.status]}>
          {PROFILE_STATUS_LABEL[profile.status]}
        </Badge>
      </td>
      <td className="voice-table__num">
        {profile.clipCount}
        {profile.excludedClipCount > 0 && <span className="voice-table__muted"> −{profile.excludedClipCount}</span>}
      </td>
      <td className="voice-table__num">{profile.matchCount}</td>
      <td className="voice-table__when">{profile.lastMatchedAt ? formatDateTime(profile.lastMatchedAt) : 'nunca'}</td>
      <td>
        <span className="voice-strength" data-level={strength.level} title={strength.advice}>
          <span className="voice-strength__meter" aria-hidden="true">
            <span className="voice-strength__fill" />
          </span>
          {strength.label}
        </span>
      </td>
    </tr>
  );
}
